import type { FocusEvent, FormEvent, KeyboardEvent, RefObject } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ChangeEvent,
  RenderInputComponentProps,
  RenderSuggestionsContainerParams,
} from 'react-autosuggest';
import Autosuggest from 'react-autosuggest';

import { AutosuggestWrapper } from '#/components/Autocomplete/Autocomplete.styled';
import type {
  InputAffixVariant,
  InputStyleVariant,
} from '#/components/Input/Input.types';
import { LoadingSection } from '#/components/LoadingSection/LoadingSection';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { SearchLocation } from '#/modules/search/types/SearchLocation';
import { REMOTE_KEYWORDS } from '#/utils/constants/search';
import {
  setGMapsError,
  useGMapsErrored,
} from '#/zustand-stores/gMapsErrorStore';

import { LocationInputSuggestion } from './LocationInputSuggestion';
import type { LocationSuggestion } from './LocationSuggestion';
import { StyledInput } from './StyledInput';
import { SuggestionsContainer } from './SuggestionsContainer';
import { geocoderResultToSearchLocation } from './geocoderResultToSearchLocation';
import { getPlacePredictions } from './getPlacePredictions';
import { getRemoteOptions } from './getRemoteOptions';

type Props = {
  onChange: (searchLocation: SearchLocation | undefined) => void;
  onClear?: () => void;
  hasSuccess?: boolean;
  qaId?: string;
  placeholder?: string;
  required?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  showLocationIcon?: boolean;
  showClearButton?: boolean;
  className?: string;
  handlePlaceDetails?: (place: google.maps.GeocoderResult) => void;
  addressTypes?: string[];
  includeCity?: boolean;
  includeFull?: boolean;
  onBlur?: (event: FocusEvent<HTMLElement>) => void;
  disabled?: boolean;
  hasRemoteOptions?: boolean;
  highlightFirstSuggestion?: boolean;
  includedRegionCodes?: string[];
  affixVariant?: InputAffixVariant;
  styleVariant?: InputStyleVariant;
  locationText: string | undefined | null;
  id: string;
};

export function LocationInput({
  hasSuccess,
  onChange,
  onClear,
  placeholder,
  required,
  inputRef: customInputRef,
  showLocationIcon = false,
  showClearButton,
  className,
  addressTypes,
  includeCity,
  includeFull,
  disabled,
  highlightFirstSuggestion = true,
  includedRegionCodes,
  hasRemoteOptions,
  locationText,
  onBlur,
  handlePlaceDetails: handlePlaceDetailsProp,
  qaId,
  id,
  affixVariant,
  styleVariant,
}: Props) {
  const { i18n, locale } = useI18n();

  const [defaultKeyword] = REMOTE_KEYWORDS[locale];
  const defaultInputRef = useRef<HTMLInputElement>(null);
  const inputRef = customInputRef ?? defaultInputRef;

  const [loading, setLoading] = useState(false);

  // This is used to keep the input state if typed before the app has fully loaded
  const [backupState, setBackupState] = useState('');

  const { gMapsErrored } = useGMapsErrored();

  const [geocoder, setGeocoder] = useState<google.maps.Geocoder>();
  const [sessionToken, setSessionToken] =
    useState<google.maps.places.AutocompleteSessionToken>();

  const [locationInputValue, setLocationInputValue] = useState(
    locationText ?? '',
  );
  const [prevPropsLocationText, setPrevPropsLocationText] =
    useState(locationText);

  useEffect(() => {
    if (locationText !== prevPropsLocationText) {
      setLocationInputValue(locationText ?? '');
      setPrevPropsLocationText(locationText);
    }
  }, [locationText, prevPropsLocationText]);

  const clearLocation = useCallback(() => {
    setLocationInputValue('');
    setBackupState('');

    if (onClear) onClear();

    onChange(undefined);
  }, [onChange, onClear]);

  const onLocationInputValueChange = (
    _: FormEvent<unknown>,
    { newValue }: ChangeEvent,
  ) => {
    if (newValue === '') {
      clearLocation();
    }

    setLocationInputValue(newValue);
  };

  const onBlurInternal = useCallback(
    (e: FocusEvent<HTMLElement>) => {
      if (onBlur) {
        return onBlur(e);
      }

      if (locationInputValue && locationInputValue !== locationText) {
        clearLocation();
      }
    },
    [onBlur, clearLocation, locationInputValue, locationText],
  );

  const loadGoogleMapsAutocomplete = useCallback(async () => {
    if (sessionToken) return;

    setLoading(true);

    try {
      const { AutocompleteSessionToken } = (await google.maps.importLibrary(
        'places',
      )) as google.maps.PlacesLibrary;
      const { Geocoder } = (await google.maps.importLibrary(
        'geocoding',
      )) as google.maps.GeocodingLibrary;
      setGeocoder(new Geocoder());
      setSessionToken(new AutocompleteSessionToken());
    } catch (error) {
      if (error instanceof Error) {
        setGMapsError(error.message);
      }
    }
  }, [sessionToken]);

  const handlePlaceDetails = useCallback(
    async (place: google.maps.GeocoderResult) => {
      // Use the formatted address, in rare cases where a suggested place isn't
      // formatted identically to its geocoded version.
      setLocationInputValue(place.formatted_address);

      if (handlePlaceDetailsProp) {
        handlePlaceDetailsProp(place);
        return;
      }

      await geocoderResultToSearchLocation(place, {
        includeCity,
        includeFull,
      }).then(onChange);
    },
    [handlePlaceDetailsProp, includeCity, includeFull, onChange],
  );

  const onSuggestionSelected = useCallback(
    async (
      _: FormEvent<HTMLInputElement>,
      { suggestion }: { suggestion: LocationSuggestion },
    ) => {
      if (!geocoder) return;

      if (suggestion.isRemoteOption) {
        onChange({
          text: null,
          stateCode: null,
          countryCode: null,
          boundingBoxN: null,
          boundingBoxW: null,
          boundingBoxS: null,
          boundingBoxE: null,
          geoType: null,
          latitude: null,
          longitude: null,
          isRemote: true,
          timezone: null,
        });

        return;
      }

      await geocoder.geocode(
        {
          placeId: suggestion.placePrediction?.placeId,
          language: locale,
        },
        (results) => {
          if (results?.length) void handlePlaceDetails(results[0]);
        },
      );
    },
    [locale, geocoder, handlePlaceDetails, onChange],
  );

  const [suggestions, setSuggestions] = useState<LocationSuggestion[] | null>(
    null,
  );

  useEffect(() => {
    if (
      inputRef.current &&
      document.activeElement === inputRef.current &&
      !sessionToken
    ) {
      setBackupState(inputRef.current.value);
    }
  }, [sessionToken, inputRef]);

  const [inProgressQuery, setInProgressQuery] = useState<
    Promise<google.maps.places.AutocompleteSuggestion[]> | null | undefined
  >(null);

  const loadSuggestions = useCallback(
    (value: string) => {
      // Cancel the previous request
      if (inProgressQuery) {
        inProgressQuery
          .then(() => {
            setTimeout(() => loadSuggestions(value), 200);
          })
          .catch(() => {
            // noop, handle errors in next query
          });
        return undefined;
      }

      if (!sessionToken) return;

      const query = getPlacePredictions({
        input: value,
        includedRegionCodes,
        addressTypes,
        sessionToken,
      });
      query
        .then((options) => {
          const remoteOptions: (LocationSuggestion | undefined)[] = [];

          if (hasRemoteOptions) {
            remoteOptions.push(
              getRemoteOptions({ locale, value, defaultKeyword }),
            );
          }

          setSuggestions(
            [...remoteOptions, ...(options || [])].filter(Boolean),
          );
        })
        .catch((error) => {
          if (error instanceof Error) {
            setGMapsError(error.message);
          }
        })
        .finally(() => {
          setInProgressQuery(null);
          setLoading(false);
          if (backupState) {
            setLocationInputValue(backupState);
          }
        });

      setInProgressQuery(query);
      return query;
    },
    [
      locale,
      addressTypes,
      backupState,
      includedRegionCodes,
      hasRemoteOptions,
      inProgressQuery,
      sessionToken,
      defaultKeyword,
    ],
  );

  const onSuggestionsFetchRequested = useCallback(
    ({ value }: { value: string }) => {
      if (sessionToken) {
        loadSuggestions(value);
      }
    },
    [sessionToken, loadSuggestions],
  );

  const renderSuggestion = useCallback(
    (suggestion: LocationSuggestion) => (
      <LocationInputSuggestion suggestion={suggestion} />
    ),
    [],
  );

  const onSuggestionsClearRequested = useCallback(() => {
    if (suggestions) {
      setSuggestions([]);
    }
    setInProgressQuery(null);
  }, [suggestions]);

  useEffect(() => {
    if (
      !sessionToken &&
      inputRef.current === document.activeElement &&
      backupState
    ) {
      loadGoogleMapsAutocomplete();
    } else if (
      inputRef.current === document.activeElement &&
      backupState &&
      !suggestions
    ) {
      loadSuggestions(backupState);
    } else if (
      inputRef.current === document.activeElement &&
      locationInputValue &&
      !suggestions
    ) {
      loadSuggestions(locationInputValue);
    }
  }, [
    sessionToken,
    backupState,
    loadGoogleMapsAutocomplete,
    loadSuggestions,
    locationInputValue,
    suggestions,
    inputRef,
  ]);

  const renderSuggestionsContainer = ({
    containerProps,
    children,
  }: RenderSuggestionsContainerParams) => {
    if (!children && (!loading || (loading && !locationInputValue)))
      return null;

    return (
      <SuggestionsContainer containerProps={containerProps} loading={loading}>
        {loading && locationInputValue ? <LoadingSection /> : children}
      </SuggestionsContainer>
    );
  };

  const renderInputComponent = useCallback(
    ({
      ref,
      autoComplete,
      className: inputClassName,
      disabled: inputDisabled,
      onBlur: inputOnBlur,
      onChange: inputOnChange,
      onFocus,
      onKeyDown,
      onKeyPress,
      title,
      value,
      ...renderInputComponentProps
    }: RenderInputComponentProps) => (
      <StyledInput
        aria-activedescendant={
          renderInputComponentProps['aria-activedescendant']
        }
        aria-autocomplete={renderInputComponentProps['aria-autocomplete']}
        aria-controls={renderInputComponentProps['aria-controls']}
        aria-label={i18n('Location')}
        autoComplete={autoComplete}
        className={inputClassName}
        disabled={inputDisabled}
        onBlur={inputOnBlur}
        onChange={inputOnChange}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        hasSuccess={hasSuccess}
        onKeyPress={onKeyPress}
        placeholder={placeholder}
        required={required}
        title={title}
        value={value}
        onClear={showClearButton ? clearLocation : null}
        gMapsErrored={gMapsErrored}
        showLocationIcon={showLocationIcon}
        styleVariant={styleVariant}
        affixVariant={affixVariant}
        qaId={qaId ?? 'location-input'}
        id={id}
        inputRef={ref}
      />
    ),
    [
      i18n,
      clearLocation,
      gMapsErrored,
      hasSuccess,
      placeholder,
      required,
      qaId,
      affixVariant,
      styleVariant,
      showClearButton,
      showLocationIcon,
      id,
    ],
  );

  const onKeyPress = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
    }
  }, []);

  const getSuggestionValue = useCallback(
    (suggestion: LocationSuggestion) =>
      suggestion.placePrediction?.text.text || '',
    [],
  );

  return (
    <AutosuggestWrapper>
      <Autosuggest
        highlightFirstSuggestion={highlightFirstSuggestion}
        suggestions={suggestions ?? []}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={getSuggestionValue}
        onSuggestionSelected={onSuggestionSelected}
        shouldRenderSuggestions={(v) =>
          Boolean(v && v.toString().trim().length >= 2)
        }
        renderInputComponent={renderInputComponent}
        renderSuggestion={renderSuggestion}
        renderSuggestionsContainer={renderSuggestionsContainer}
        inputProps={{
          className,
          disabled,
          onChange: onLocationInputValueChange,
          onBlur: onBlurInternal,
          onFocus: loadGoogleMapsAutocomplete,
          onKeyPress,
          placeholder,
          required,
          value: locationInputValue || backupState || '',
          title: i18n('Location'),
          ref: inputRef,
        }}
      />
    </AutosuggestWrapper>
  );
}
